import React, { useState } from 'react';

const GALAXIES = [
  'fundamentals',
  'version-control',
  'cloud-computing',
  'containerization',
  'ci-cd',
  'infrastructure-as-code',
  'monitoring-logging',
  'security',
  'scaling-optimization'
];

const GalaxyContent = {
  fundamentals: {
    title: "Fundamentals Galaxy",
    icon: "💻",
    color: "#4CAF50",
    levels: {
      beginner: [
        {
          title: "Basic Command Line Usage",
          skills: ["Navigation commands", "File manipulation", "Process management"]
        },
        {
          title: "Operating Systems Basics",
          skills: ["Linux file system", "User management", "Package management"]
        },
        {
          title: "Networking Fundamentals",
          skills: [
            "OSI model",
            "IP addressing and subnetting",
            "DNS and DHCP",
            "Firewalls and security",
            "Network protocols (TCP/UDP, HTTP/HTTPS)",
            "VLANs and routing basics"
          ]
        },
        {
          title: "Basic Scripting",
          skills: ["Bash scripting", "Python basics"]
        }
      ],
      intermediate: [
        "Advanced Linux system administration",
        "Networking protocols and architecture",
        "Web servers (Nginx, Apache)",
        "Database basics (SQL, NoSQL)",
      ],
      advanced: [
        "System design principles",
        "Distributed systems concepts",
        "Performance tuning",
        "Troubleshooting methodologies",
      ],
    },
  },
  "version-control": {
    title: "Version Control Galaxy",
    icon: "🌿",
    color: "#F05032",
    levels: {
      beginner: ["Basic Git commands", "Understanding repositories"],
      intermediate: ["Advanced Git workflows", "Code review processes"],
      advanced: ["Git hooks and automation", "Managing large repositories"],
    },
  },
  "cloud-computing": {
    title: "Cloud Computing Galaxy",
    icon: "☁️",
    color: "#FF9800",
    levels: {
      beginner: ["Cloud concepts and benefits", "Major cloud providers overview"],
      intermediate: ["Cloud architecture patterns", "Serverless computing"],
      advanced: ["Multi-cloud strategies", "Cloud-native application design"],
    },
  },
  containerization: {
    title: "Containerization Galaxy",
    icon: "🐳",
    color: "#2196F3",
    levels: {
      beginner: ["Container concepts", "Basic Docker commands"],
      intermediate: ["Docker Compose", "Container orchestration basics"],
      advanced: ["Advanced Kubernetes concepts", "Service mesh"],
    },
  },
  "ci-cd": {
    title: "CI/CD Galaxy",
    icon: "🚀",
    color: "#9C27B0",
    levels: {
      beginner: ["CI/CD concepts", "Basic pipeline creation"],
      intermediate: ["Advanced pipeline configurations", "Deployment strategies"],
      advanced: ["Pipeline as Code", "Custom build agents"],
    },
  },
  "infrastructure-as-code": {
    title: "Infrastructure as Code Galaxy",
    icon: "🏗️",
    color: "#607D8B",
    levels: {
      beginner: ["IaC concepts", "Basic Terraform usage"],
      intermediate: ["Advanced Terraform", "Ansible for configuration management"],
      advanced: ["Custom providers and resources", "GitOps workflows"],
    },
  },
  "monitoring-logging": {
    title: "Monitoring & Logging Galaxy",
    icon: "📊",
    color: "#FFC107",
    levels: {
      beginner: ["Monitoring concepts", "Basic logging practices"],
      intermediate: ["Advanced monitoring tools", "Log aggregation"],
      advanced: ["Distributed tracing", "Anomaly detection"],
    },
  },
  security: {
    title: "Security Galaxy",
    icon: "🔒",
    color: "#FF5722",
    levels: {
      beginner: ["Basic security concepts", "OWASP Top 10"],
      intermediate: ["Security in CI/CD pipelines", "Cloud security best practices"],
      advanced: ["Threat modeling", "Zero Trust architecture"],
    },
  },
  "scaling-optimization": {
    title: "Scaling & Optimization Galaxy",
    icon: "📈",
    color: "#00BCD4",
    levels: {
      beginner: ["Scalability concepts", "Load balancing basics"],
      intermediate: ["Autoscaling configurations", "Database optimization"],
      advanced: ["Microservices architecture", "Global scale applications"],
    },
  },
};

const StarField = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {[...Array(100)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${Math.random() * 2 + 1}px`,
            height: `${Math.random() * 2 + 1}px`,
            animation: `twinkle ${Math.random() * 5 + 5}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
};

const DevOpsRoadmap = () => {
  const [currentGalaxy, setCurrentGalaxy] = useState(null);
  const [currentLevel, setCurrentLevel] = useState('beginner');
  const [currentTopic, setCurrentTopic] = useState(null);

  const handleSelectGalaxy = (galaxy) => {
    setCurrentGalaxy(galaxy);
    setCurrentLevel('beginner');
    setCurrentTopic(null);
  };

  const handleSelectLevel = (level) => {
    setCurrentLevel(level);
    setCurrentTopic(null);
  };

  const handleSelectTopic = (topic) => {
    setCurrentTopic(topic);
  };

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      <StarField />
      <div className="absolute inset-0 overflow-auto py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 text-center">DevOps Galaxy Map</h1>
          {currentGalaxy ? (
            <div className="bg-gray-900 rounded-lg p-8 shadow-lg">
              <div className="flex items-center mb-6">
                <div className="mr-4 text-4xl">{GalaxyContent[currentGalaxy].icon}</div>
                <h2 className="text-3xl font-bold">{GalaxyContent[currentGalaxy].title}</h2>
              </div>
              <div className="flex mb-6">
                {['beginner', 'intermediate', 'advanced'].map((level) => (
                  <button
                    key={level}
                    onClick={() => handleSelectLevel(level)}
                    className={`mr-4 px-4 py-2 rounded-full ${
                      currentLevel === level ? 'bg-blue-600' : 'bg-gray-800'
                    }`}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
              {currentTopic ? (
                <div>
                  <h3 className="text-2xl font-bold mb-4">{currentTopic.title}</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {currentTopic.skills.map((skill, index) => (
                      <li key={index}>{skill}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setCurrentTopic(null)}
                    className="mt-6 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-full transition duration-300 ease-in-out"
                  >
                    Back to Topics
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {GalaxyContent[currentGalaxy].levels[currentLevel].map((item, index) => (
                    <li key={index} className="flex items-center">
                      {typeof item === 'string' ? (
                        <>
                          <span className="mr-2 text-green-400">✓</span>
                          {item}
                        </>
                      ) : (
                        <button
                          onClick={() => handleSelectTopic(item)}
                          className="text-left hover:text-blue-400 transition duration-300"
                        >
                          <span className="mr-2 text-green-400">✓</span>
                          {item.title}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <button
                onClick={() => setCurrentGalaxy(null)}
                className="mt-8 bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-full transition duration-300 ease-in-out"
              >
                Back to Galaxy Map
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {GALAXIES.map((galaxy) => (
                <div
                  key={galaxy}
                  className="bg-gray-900 rounded-lg p-6 cursor-pointer hover:bg-gray-800 transition duration-300 ease-in-out"
                  onClick={() => handleSelectGalaxy(galaxy)}
                >
                  <div className="flex items-center mb-4">
                    <div className="mr-4 text-3xl">{GalaxyContent[galaxy].icon}</div>
                    <h3 className="text-xl font-bold">{GalaxyContent[galaxy].title}</h3>
                  </div>
                  <p className="text-gray-400">Explore {GalaxyContent[galaxy].title.toLowerCase()} concepts and skills.</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevOpsRoadmap;